/* eslint-disable @typescript-eslint/no-explicit-any */
import { computed, Injectable, signal, ViewContainerRef } from '@angular/core';
import { Observable } from 'rxjs';

import { Maybe } from '../../types/base/';

type ModalState = Record<string, any>;
// type OnConfirmCallback = (...args:unknown[])=>void;

@Injectable({
  providedIn: 'root',
})
export class ModalService {
  #current = signal<Maybe<string>>(undefined);
  #state = signal<Maybe<ModalState>>(undefined);

  current = this.#current.asReadonly();
  state = this.#state.asReadonly();

  /** SIMPLY set id as current
   * - expect you to render the modal based on the current value
   * - you may add on close callback
   * - and even store current state object (*ease passing data to the modal*)
   *
   * ```html
   * this.#modalService.show('my-modal-id', { props: 'can store extra state in service' });
   * ...
   * @if(modalService.current() === 'my-modal-id') {
   *   <evc-modal>
   *     {{modalService.state().props}}
   *   </evc-modal>
   * }
   * ```
   */
  show(modal: string, state?: ModalState /* , onConfirm?:OnConfirmCallback*/): void {
    this.#current.set(modal);
    if (state) this.#state.set(state);
  }

  close(): void {
    this.#current.set(undefined);
    this.#dynamicModal()?.destroy();
    this.#state.set({});
  }

  /** DYNAMIC MODAL SYSTEM */

  #viewContainerRef = signal<Maybe<ViewContainerRef>>(undefined);

  /** Register the slot to render dynamic modals
   * - automaticaly registered in platform component
   */
  registerViewContainerRef(ref: ViewContainerRef): void {
    this.#viewContainerRef.set(ref);
  }

  #dynamicModal = signal<
    | {
        component: any;
        config?: { data?: unknown; [key: string]: unknown };
        componentRef: ReturnType<ViewContainerRef['createComponent']>;
        destroy: () => void;
      }
    | undefined
  >(undefined);

  /** RENDER a Modal component inside our modal host
   * - **component** should extends EvcModal
   *   - `this.subject.next(value)` when submit done (or .error / .complete)
   *   - Tips: doSomeObservable$(payload).subscribe(this.subject)` is best to link form with modal and send both success/errors
   * - **config** is provided to the components as props (setInput or instance[key]=value)
   *    - Either modal props (width, title...) or your component extra ones
   *    - Tips: use `action$ = input<Action>()` then subscribe to it to send subject
   *    - Caution: do not forget to provide all translated texts; even for actions
   *
   * ```ts
   * this.#modalService
   *  .open$(YourModalComponent, { props })
   *  .subscribe({
   *     next: (result) => {  }, // confirmn success
   *     error: (error) => {  }, // confirmn error
   *     complete: () => {  }, // modal close
   *  });
   * ``` */
  open$<T>(
    component: new (...args: unknown[]) => any,
    config: Record<string, unknown> = {},
  ): Observable<T> {
    const modal = this.#createDynamicComponent<T>(component, config)!;

    return modal.subject;
  }

  #createDynamicComponent<T>(
    component: new (...args: unknown[]) => T,
    config: Record<string, unknown> = {},
  ):
    | {
        component: new (...args: unknown[]) => T;
        config?: { data?: unknown; [key: string]: unknown };
        componentRef: ReturnType<ViewContainerRef['createComponent']>;
        destroy: () => void;
        subject: Observable<T>;
      }
    | undefined {
    const viewContainerRef = this.#viewContainerRef();
    if (!viewContainerRef) throw new Error('EvcModalService: No ViewContainerRef registered.');

    viewContainerRef.clear();

    const componentRef = viewContainerRef.createComponent(component);
    const componentRefInstance = componentRef.instance as Record<string, unknown>;
    for (const [key, value] of Object.entries(config ?? {})) {
      if (typeof componentRefInstance[key] === 'function') {
        componentRef.setInput(key, value);
      } else {
        componentRefInstance[key] = value;
      }
    }

    const destroy = () => {
      viewContainerRef.clear();
      componentRef.destroy();
      this.#dynamicModal.set(undefined);
    };

    const dynamicModal = {
      component,
      config,
      componentRef,
      destroy,
      subject: componentRefInstance.subject as unknown as Observable<T>,
    };
    this.#dynamicModal.set(dynamicModal);

    return dynamicModal;
  }

  /** LEGACY ALIASES */

  currentShownModal = computed(() => {
    console.warn('[modal.service] currentShownModal is deprecated, use current() instead.');

    return this.current();
  });

  showCurrentModal(modal: string): void {
    console.warn('[modal.service] showCurrentModal is deprecated, use show() instead.');
    this.show(modal);
  }

  closeCurrentModal(): void {
    console.warn('[modal.service] closeCurrentModal is close, use show() instead.');
    this.close();
  }
}
